import * as d3 from 'd3';
import { STYLES } from '../styles/constants';
import { editHeader } from '../utils/headerEditor';
import { SwimlaneData, EditMode } from '../types';
import { createRowContextMenu } from './menus/RowContextMenu';

export function renderSubHeaders(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  data: SwimlaneData,
  onSave: (data: SwimlaneData) => void,
  editMode: EditMode,
  onRowOperation: (index: number, operation: 'up' | 'down' | 'delete' | 'moveUp' | 'moveDown') => void,
  t: (key: string) => string
) {
  const subHeaderContainer = container
    .append('div')
    .attr('class', 'sub-headers')
    .style('display', 'flex')
    .style('flex-direction', 'column');

  // Trouver le conteneur de scroll en remontant dans le DOM
  const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    let current: HTMLElement | null = element;
    while (current) {
      const style = window.getComputedStyle(current);
      if (style.overflowX === 'scroll' || style.overflowX === 'auto') {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  const containerNode = container.node();
  const scrollContainer = findScrollContainer(containerNode);
  
  // Stocker les références des side-headers pour les mettre à jour lors du scroll
  const sideHeaders: HTMLElement[] = [];

  data.subHeaderRows?.headers.forEach((header, index) => {
    const lane = subHeaderContainer
      .append('div')
      .attr('class', 'lane')
      .style('width', '100%')
      .style('height', `${STYLES.header.laneHeight}px`)
      .style('border-left', `1px solid ${STYLES.colors.border}`)
      .style('border-right', `1px solid ${STYLES.colors.border}`)
      .style('border-bottom', `1px solid ${STYLES.colors.border}`)
      .style('border-top', index === 0 ? `1px solid ${STYLES.colors.border}` : 'none')
      .style('background-color', STYLES.colors.background)
      .style('position', 'relative');

    const sideHeader = lane
      .append('div')
      .attr('class', 'side-header')
      .style('width', `${STYLES.header.sideHeaderWidth}px`)
      .style('height', '100%')
      .style('position', 'absolute')
      .style('left', '0')
      .style('top', '0')
      .style('background-color', STYLES.colors.background)
      .style('border-right', `1px solid ${STYLES.colors.border}`)
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('z-index', '10');
    
    const sideHeaderNode = sideHeader.node() as HTMLElement;
    if (sideHeaderNode) {
      sideHeaders.push(sideHeaderNode);
    }

    const headerContent = sideHeader
      .append('div')
      .style('position', 'relative')
      .style('width', '100%')
      .style('height', '100%')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center');

    if (editMode.enabled) {
      const contextMenuTrigger = headerContent
        .append('div')
        .attr('class', 'context-menu-trigger')
        .style('position', 'absolute')
        .style('top', '4px')
        .style('right', '4px')
        .style('cursor', 'pointer')
        .style('padding', '4px')
        .style('border-radius', '4px')
        .style('display', 'flex')
        .style('gap', '2px')
        .style('z-index', '1')
        .style('opacity', '0')
        .style('transition', 'opacity 0.2s')
        .on('click', (event) => {
          event.stopPropagation();
          createRowContextMenu(
            headerContent,
            { x: event.pageX, y: event.pageY },
            {
              onAddRowUp: () => onRowOperation(index, 'up'),
              onAddRowDown: () => onRowOperation(index, 'down'),
              onMoveRowUp: () => onRowOperation(index, 'moveUp'),
              onMoveRowDown: () => onRowOperation(index, 'moveDown'),
              onDeleteRow: () => onRowOperation(index, 'delete')
            },
            t,
            index,
            data.subHeaderRows?.headers.length
          );
        });

      // Create the three dots
      for (let i = 0; i < 3; i++) {
        contextMenuTrigger.append('div')
          .style('width', '4px')
          .style('height', '4px')
          .style('background-color', '#666')
          .style('border-radius', '50%')
      }

      // Show/hide menu trigger on hover
      headerContent
        .on('mouseenter', function () {
          contextMenuTrigger.style('opacity', '1');
        })
        .on('mouseleave', function () {
          contextMenuTrigger.style('opacity', '0');
        });
    }

    // Modified text rendering approach
    const textContainer = headerContent
      .append('div')
      .attr('class', 'header-text')
      .style('position', 'relative')
      .style('height', '100%')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center');

    const displayText = header.length > 15 ? header.slice(0, 15) + '...' : header;

    textContainer
      .append('div')
      .style('transform', 'rotate(-90deg)')
      .style('transform-origin', 'center')
      .style('text-overflow', 'ellipsis')
      .style('white-space', 'nowrap')
      .style('color', STYLES.colors.text)
      .style('cursor', 'pointer')
      .style('padding', '5px')
      .text(displayText)
      .on('click', (event) => {
        editHeader(event.target as HTMLElement, index, data, onSave);
      });

    // Main lane area
    lane
      .append('div')
      .attr('class', 'lane-content')
      .style('margin-left', `${STYLES.header.sideHeaderWidth}px`)
      .style('height', '100%')
      .style('position', 'relative')
      .style('background-color', STYLES.colors.background);
  });
  
  // Gestionnaire de scroll pour rendre les side-headers fixes
  if (scrollContainer && sideHeaders.length > 0) {
    // Fonction pour obtenir le scale actuel depuis le container parent
    const getCurrentScale = (): number => {
      const containerElement = containerNode;
      if (!containerElement) return 1;
      
      // Chercher le conteneur avec le transform scale (swimlane-container)
      // Le scale est appliqué sur l'élément avec id="swimlane-container"
      let current: HTMLElement | null = containerElement;
      
      // D'abord, chercher l'élément swimlane-container qui contient le scale
      const swimlaneContainer = document.getElementById('swimlane-container');
      if (swimlaneContainer) {
        const transform = window.getComputedStyle(swimlaneContainer).transform;
        if (transform && transform !== 'none') {
          // Extraire le scale depuis la matrice de transformation
          // transform: scale(x) donne une matrice comme "matrix(x, 0, 0, x, 0, 0)"
          const matrix = transform.match(/matrix\(([^)]+)\)/);
          if (matrix) {
            const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
            // La première valeur de la matrice est le scaleX
            if (values.length >= 1 && !isNaN(values[0])) {
              return values[0];
            }
          }
          // Si c'est un scale() simple, essayer de l'extraire
          const scaleMatch = transform.match(/scale\(([^)]+)\)/);
          if (scaleMatch) {
            const scaleValue = parseFloat(scaleMatch[1]);
            if (!isNaN(scaleValue)) {
              return scaleValue;
            }
          }
        }
      }
      
      // Fallback : chercher dans la hiérarchie parente
      while (current && current !== scrollContainer) {
        const transform = window.getComputedStyle(current).transform;
        if (transform && transform !== 'none') {
          const matrix = transform.match(/matrix\(([^)]+)\)/);
          if (matrix) {
            const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
            if (values.length >= 1 && !isNaN(values[0])) {
              return values[0];
            }
          }
        }
        current = current.parentElement;
      }
      
      return 1;
    };

    const updateSideHeadersPosition = () => {
      if ((containerNode as HTMLElement & { _sideHeadersScrollPaused?: boolean })._sideHeadersScrollPaused) {
        return;
      }
      const scrollLeft = scrollContainer.scrollLeft;
      const scale = getCurrentScale();
      // Ajuster le scrollLeft en fonction du scale
      // Quand le parent a transform: scale(x), les enfants sont aussi zoomés
      // Donc translateX(100px) avec scale(2) = 200px visuellement
      // Pour compenser, on doit diviser par le scale pour que le déplacement visuel soit correct
      const adjustedScrollLeft = scrollLeft / scale;
      
      sideHeaders.forEach((sideHeader) => {
        sideHeader.style.transform = `translateX(${adjustedScrollLeft}px)`;
      });
    };
    
    // Mettre à jour la position initiale
    updateSideHeadersPosition();
    
    // Écouter les événements de scroll
    scrollContainer.addEventListener('scroll', updateSideHeadersPosition, { passive: true });
    
    // Écouter aussi les changements de zoom (via MutationObserver ou événement personnalisé)
    // Observer les changements de style sur le container pour détecter le zoom
    const observer = new MutationObserver(() => {
      updateSideHeadersPosition();
    });
    
    // Observer les changements d'attributs style sur le swimlane-container qui contient le scale
    const swimlaneContainer = document.getElementById('swimlane-container');
    if (swimlaneContainer) {
      observer.observe(swimlaneContainer, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: false
      });
    } else if (containerNode) {
      // Fallback : observer le container node
      observer.observe(containerNode, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: false
      });
    }
    
    // Écouter aussi les événements de resize qui peuvent affecter le scale
    window.addEventListener('resize', updateSideHeadersPosition, { passive: true });
    
    // Nettoyer les événements lors du prochain rendu
    // Note: Dans un vrai cas, il faudrait gérer le nettoyage proprement via un cleanup
    // Pour l'instant, on stocke les références pour un nettoyage futur
    (containerNode as any)._subHeadersCleanup = () => {
      scrollContainer.removeEventListener('scroll', updateSideHeadersPosition);
      window.removeEventListener('resize', updateSideHeadersPosition);
      observer.disconnect();
    };
  }
}